import { HandoverService } from '@tableau/ai-search';
import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import _ from 'underscore';

import { CommonErrorCode } from '~commonInfra/utils/ErrorCodes';
import { emitAdminInsightsContentEvent } from '~src/adminInsights/telemetry/AdminInsightsPftEmitter';
import { setPreviousUrl } from '~src/bootstrap/redux/actions/SetPreviousUrlReducerAction';
import { VizPathInitialState } from '~src/bootstrap/redux/reducers/VizPathReducer';
import { SessionSiteSelectors, SessionUserSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { selectVizPath } from '~src/bootstrap/redux/selectors/VizPathSelector';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { RouterError, RouterErrorOwnProps } from '~src/bootstrap/routerHelpers/RouterError';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { BrowserTitleService } from '~src/commonInfra/services/BrowserTitleService';
import { WindowLocationService } from '~src/commonInfra/services/WindowLocationService';
import { isCurrentUrlViz } from '~src/commonInfra/url/UrlHelpers';
import { UrlParamKeys } from '~src/commonInfra/url/UrlParamKeys';
import { UrlTelemetryService } from '~src/commonInfra/url/UrlTelemetryService';
import { MapDispatchToProps, MapStateToProps } from '~src/commonInfra/utils/ConnectUtils';
import { IDataAlertInfo } from '~src/dda/models/IDataAlertInfo';
import { DataAlerts } from '~src/dda/resources/DataAlerts';
import { callViaApiService, GetLocationForContentRequestParams, GetLocationForContentResponse } from '~src/httpClient/restApi/location/getLocationForContent';
import { Projects } from '~src/projects/resources/Projects';
import { RequestAccessAction } from '~src/requestAccess/actions/RequestAccessAction';
import { RequestAccessError } from '~src/requestAccess/components/RequestAccessError';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { dataDrivenAlertNotificationType, editAlertLinkType, sendReturnFromSlackTelemetry, viewLinkType } from '~src/settings/IntegrationsSettings/slackConnectivity/SlackIntegrationSettingsTelemetry';
import { ServerSettingsUnauthenticated } from '~src/settings/services/ServerSettingsUnauthenticated';
import { enterSpace, setSpaceInfoItem } from '~src/spacesPlatform/actions/EnterSpaceAction';
import {
  addContentTypeToProjectHierarchy,
  populateBreadcrumbsForPS,
  populateContentBreadcrumbs
} from '~src/spacesPlatform/components/breadcrumbs/BreadcrumbHelpers';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { maybeSendTelemetry_favoriteItemOpened } from '~src/spacesPlatform/components/spaceComponents/SpaceTelemetry';
import { IExplorableItemTypeInfo, INamedItemInfo, IOwnedItemInfo, ISpaceTypeInfo } from '~src/spacesPlatform/models/resourceInfoParts';
import { selectLayoutType } from '~src/spacesPlatform/selectors/LayoutTypeSelector';
import { LayoutType } from '~src/spacesPlatform/state/LayoutType';
import { SpaceInfo } from '~src/spacesPlatform/state/SpaceInfo';
import { IViewDetailInfo } from '~src/views/models/IViewInfo';
import { Views } from '~src/views/resources/Views';
import { IViewActions } from '~src/views/ServerApi/types/IViewActions';
import { setVizPath } from '~src/viz/actions/SetVizPathAction';
import { fillViewPort, vizClass, vizContainerStyleClass, vizSpaceVizStyle, vizWithBreadcrumbStyle } from '~src/viz/styles/VizViewerStyles';

import { sendVisitAlertFromEmailTelemetry } from '../helpers/VizSpaceTelemetry';
import { VizUrl } from '../helpers/VizUrlHelpers';
import { IViz, VizType } from '../models/Viz';
import { VizLoadContextService, VizLoadType } from '../services/VizLoadContextService';
import { VizService } from '../services/VizService';
import { VizView } from './Viz';

/**
 * Safely decode a URI component, returning the original string if decoding fails.
 * This handles cases where the string may already be decoded or contain invalid sequences.
 */
function safeDecodeURIComponent(str: string): string {
  try {
    return decodeURIComponent(str);
  } catch (e) {
    return str;
  }
}

/**
 * Normalize a viz path for comparison by decoding it.
 * This ensures encoded paths (e.g., with %E6%97%A5 for Kanji) can be compared
 * with decoded paths (e.g., with raw Unicode 日).
 */
function normalizeVizPathForComparison(path: string): string {
  return safeDecodeURIComponent(path);
}


/**
 * This class renders tbViz, which is the container for a viz.
 * VPC and the viz communicate via the url and the parameters set on it. For example, when the viz is done
 * loading it sends a message and we update the url to have the instance query parameter (:iid=#). A lot of
 * java E2E tests wait for this to know that the viz is ready for interaction.
 * tbViz also creates the viz url for the _iframe_ that holds the viz. For example, it adds a device parameter
 * just for the iframe so the viz knows which device-specific-dashboard to show.
 * When a user navigates to: http://localhost:4000/#/views/DataAlertsWorkbook/DSDDashboard?:editDataAlert=89&:original_view=y&:iid=17
 * The iframe url might be:
 *  http://localhost:4000/views/DataAlertsWorkbook/DSDDashboard?:embed=y&:showVizHome=n&:toolbar=top&%3AeditDataAlert=89&%3Aoriginal_view=y&%3Adevice=tablet&%3AopenAuthoringInTopWindow=true&%3AbrowserBackButtonUndo=true&%3AcommentingEnabled=true&%3AreloadOnCustomViewSave=true&%3AshowAppBanner=false&%3AisVizPortal=true&:apiID=host1#navType=0&navSrc=Opt
 * which specifies information about commenting, device, and more.
 * It's very delicate and some url parameters should cause a re-render of the whole viz page, and some should not.
 * That's why this class has so much code.
 */

export interface VizSpaceProviderOwnProps {
  readonly vizPathFromUrl: string;
  readonly routeLocationSearch: string;
  readonly urls: SpacesSiteUrls;
  readonly grantAccessDisplay: boolean;
  readonly grantAccessUrl: string;
}

export interface VizSpaceProviderStateProps {
  readonly vizPath: string;
  readonly sessionSiteUrlName: string;
  readonly layoutType: LayoutType;
  readonly isRequestAccessEnabled: boolean;
}

export interface VizSpaceProviderDispatchProps {
  readonly setVizPath: (vizPath: string) => void;
  readonly setSpaceInfoItem: (spaceInfo: SpaceInfo) => void;
  readonly setPreviousUrl: (previousUrl: string) => void;
}

export interface VizSpaceProviderState {
  readonly viewInfo: IViewDetailInfo;
  readonly errorCode: number;
  readonly hasRoutingError: boolean;  // routing error only!
  readonly errorVizPath: string;
  readonly hasDataAlertError: boolean;
  readonly dataAlertRouterErrorProps: RouterErrorOwnProps;
  readonly dataAlert: IDataAlertInfo;  // only if editing a data alert
  readonly initialRender: boolean;
}

const initialNoErrorState: VizSpaceProviderState = {
  viewInfo: undefined,
  errorCode: undefined,
  hasRoutingError: false,
  hasDataAlertError: false,
  dataAlertRouterErrorProps: undefined,
  errorVizPath: undefined,
  dataAlert: undefined,
  initialRender: true,
};

export interface IVizProperties {
  view?: IViewDetailInfo;
}

interface VizViewerProps {
  viz: IViz;
  vizProps: IVizProperties;
  allowedAction: IViewActions;
  dataAlert: IDataAlertInfo;
  dalert: IDataAlertInfo;
  firstViewPathOfPageLoad: IViewDetailInfo;

  // used to display a RouterError
  // for the 'dont-have-access' errors
  displayRoutingError: boolean;  // Viz-not-found, NOT data alerts errors!
  displayDataAlertRoutingError: boolean;

  updateVizProviderState: (newState: Partial<VizSpaceProviderState>) => void;
  onVizPathChange: (newPath: string) => void;
  onVizInstanceIdChange: (newInstanceId: string) => void;
  onVizIsExplicitOriginalVizChange: (isExplicitOriginalViz: boolean) => void;
  clearDataAlertRoute: () => void;
}

export type VizSpaceProviderProps = VizSpaceProviderOwnProps & VizSpaceProviderStateProps & VizSpaceProviderDispatchProps;

export class VizSpaceProvider extends React.Component<VizSpaceProviderProps, VizSpaceProviderState> {

  private vizViewProps: VizViewerProps = null;
  private unmounted: boolean = false;
  private viz: tb.IViz = undefined; // the actual viz object
  private vizContainer: HTMLElement; // the viz container
  private vizInitialized = false;
  private server = getSessionManagementServiceInstance();
  private vizService = new VizService();
  // Tracks the last view (sheet) luid we emitted an Admin Insights PFT 'view' event for,
  // so repeated path-change callbacks for the same sheet (e.g. the spurious CUSTOM_VIEW_*
  // events, or initial-load followed by an early TAB_SWITCH) don't double-count it.
  private lastEmittedAdminInsightsViewLuid: string = undefined;

  public constructor(props: VizSpaceProviderProps) {
    super(props);
    this.state = initialNoErrorState;
  }

  private renderVizContainer() {
    // this structure is intended to keep the same structure of the existing viz container
    // changes to this will easily break tests
    return (
      <div style={vizWithBreadcrumbStyle} id={'viz-container'} className={vizContainerStyleClass}>
        <div style={fillViewPort}>
          <div ref={c => this.vizContainer = c} style={vizSpaceVizStyle} className={vizClass} id={'viz'} tb-test-id={'viz'}> </div>
        </div>
      </div>);

  }

  private loadViz() {
    this.viz && this.viz.dispose();
    this.viz = VizView.loadViz(this.vizViewProps || this.initVizViewerProps(), this.vizContainer);
    this.vizInitialized = true;
  }

  public render() {
    const dataAlertIdFromUrl: string = VizUrl.getVizParams(this.props.routeLocationSearch)[UrlParamKeys.EditDataAlert];

    if (this.state.hasDataAlertError) {
      this.vizInitialized = false;
      return <RouterError {...this.state.dataAlertRouterErrorProps} />;

    } else if (this.state.hasRoutingError) {
      this.vizInitialized = false;
      if (this.props.isRequestAccessEnabled) {
        if (this.state.errorCode === CommonErrorCode.CallingUserInsufficientPermissions) {
          return <Redirect to={`${this.props.urls.homePath()}${this.props.urls.viewRequestAccessPath(this.props.vizPathFromUrl)}`} />;
        } else {
          return <RequestAccessError
            url={this.props.urls.exploreUrl()}
            contentIdentifier={this.state.errorVizPath}
            contentType={'view'}
            errorCode={this.state.errorCode} />;
        }
      }

      // old error page
      return <RouterError urls={this.props.urls} errorType="view" />;

    } else if (!this.state.viewInfo) {
      this.vizInitialized = false;
      return null;

    } else if (!_.isUndefined(dataAlertIdFromUrl) && (_.isUndefined(this.state.dataAlert) || this.state.dataAlert.id !== dataAlertIdFromUrl)) {
      this.vizInitialized = false;
      // don't render viz if we are editing a data alert until the data alert for the current data alert id (in the url)
      // is fetched, so that we don't render the wrong viz for the alert device type
      return null;
    }

    // have view info and no errors, render the viz
    if (!this.vizViewProps) {
      this.initVizViewerProps();
    }
    return this.renderVizContainer();
  }

  public async componentDidMount() {
    if (FeatureFlags.ces_AISearchEnabled && SessionUserSelectors.canUseAISearch(getAppStore()!.getState()) && FeatureFlags.analyticsAssistantDashboardQAEnabled) {
      HandoverService.handoverToCABU();
    }

    if (!VizLoadContextService.has()) {
      const navEntry = window.performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
      // Set viz load type to BrowserRefresh when the page is reloaded
      if (navEntry?.type === 'reload') {
        VizLoadContextService.set(VizLoadType.BrowserRefresh);
      } else {
        const params = VizUrl.getVizParams(this.props.routeLocationSearch);
        const slackLinkSrc = params[UrlParamKeys.LinkFromSlack];
        const fromEmail = params[UrlParamKeys.FromFailureEmail] || params[UrlParamKeys.FromSuccessEmail];
        if (slackLinkSrc) {
          VizLoadContextService.set(VizLoadType.SlackLink);
        } else if (fromEmail) {
          VizLoadContextService.set(VizLoadType.EmailLink);
        } else {
          VizLoadContextService.set(VizLoadType.DirectUrl);
        }
      }
    }

    // set initial viz path
    this.props.setVizPath(this.props.vizPathFromUrl);
    const firstViewInfoOfPageLoad = await this.getViewInfoAndUpdateBrowserTitleAndGetDataAlert();
    if (firstViewInfoOfPageLoad) {
      // make sure to update breadcrumbs with initial view info
      this.updateBreadcrumbs(firstViewInfoOfPageLoad);
    }

    if (this.vizContainer && !this.vizInitialized) {
      this.loadViz();
    }

    // If the URL was accessed from Slack, the :linkFromSlack param key is present, and the value describes one of the following sources:
    // commenting mention, data driven alert, sharing, edit data alert (update if more are added)
    let slackLinkSrc = VizUrl.getVizParams(this.props.routeLocationSearch)[UrlParamKeys.LinkFromSlack];
    if (slackLinkSrc) {
      // If the source is the 'edit data alert' action, the notificationType should be classified as 'dataDrivenAlert',
      // and the link type should become 'editAlert'
      let linkType = (slackLinkSrc === editAlertLinkType) ? editAlertLinkType : viewLinkType;
      slackLinkSrc = (slackLinkSrc === editAlertLinkType) ? dataDrivenAlertNotificationType : slackLinkSrc;
      sendReturnFromSlackTelemetry({
        userId: this.server.sessionUserId(),
        siteLuid: this.server.sessionSiteLuid(),
        notificationType: slackLinkSrc,
        linkType: linkType
      });
      WindowLocationService.deleteQueryParameter(UrlParamKeys.LinkFromSlack);
    }

  }

  public componentWillUnmount() {
    this.unmounted = true;
    this.props.setVizPath(''); // reset store

    if (FeatureFlags.ces_AISearchEnabled && SessionUserSelectors.canUseAISearch(getAppStore()!.getState()) && FeatureFlags.analyticsAssistantDashboardQAEnabled) {
      HandoverService.stopHandoverToCABU();
    }

    this.viz && this.viz.dispose();
  }

  private getViewInfoAndUpdateBrowserTitleAndGetDataAlert = async () => {
    try {
      const viewInfo: IViewDetailInfo = await Views.getByPath(this.props.vizPathFromUrl);
      BrowserTitleService.setVizTitle(viewInfo.name, viewInfo.workbook.name);
      this.props.setSpaceInfoItem({ item: viewInfo, spaceType: 'view', subspaceName: undefined });
      // PFT: emit per-sheet Admin Insights 'view' telemetry for the initial (default) sheet.
      this.maybeEmitAdminInsightsViewEvent(viewInfo);

      try {
        if (!_.isUndefined(viewInfo)) {
          const currDataAlertId = VizUrl.getVizParams(this.props.routeLocationSearch)[UrlParamKeys.EditDataAlert];
          if (currDataAlertId) {
            // data alert info needs to be fetched before we update the state and render the viz
            const dataAlert: IDataAlertInfo = await DataAlerts.getById(currDataAlertId);
            this.handleGetDataAlertResponse(dataAlert, viewInfo);
          } else {

            // not editDataAlert parameter so just set the viewInfo on the state
            this.updateState({ ...initialNoErrorState, viewInfo: viewInfo });
          }
        }
        return viewInfo;

      } catch (e) {
        this.handleGetDataAlertReturnsError(viewInfo);
        return viewInfo;
      }

    } catch (errorCode) {
      // fetching viewInfo failed-- show view routing error
      this.handleViewGetByPathError(errorCode,
        () => this.updateState({
          viewInfo: undefined,
          errorCode: errorCode,
          errorVizPath: this.props.vizPathFromUrl,
          hasRoutingError: true,
          hasDataAlertError: false,
          dataAlertRouterErrorProps: undefined,
          dataAlert: undefined,
          initialRender: true
        }),
        this.props.vizPathFromUrl);
      return undefined;
    }
  };

  private getVizParamsThatRequireReload(routeLocationSearch: string): _.Dictionary<any> {
    let params = VizUrl.getVizParams(routeLocationSearch);
    params = _.omit(params, [UrlParamKeys.OriginalView]);
    return params;
  }

  public async componentDidUpdate(prevProps: VizSpaceProviderProps, prevState: VizSpaceProviderState) {
    const prevVizPathFromUrl = prevProps.vizPathFromUrl;
    const currVizPathFromUrl = this.props.vizPathFromUrl;
    const vizPathFromAppState = this.props.vizPath;

    // Normalize paths for comparison to handle URL encoding differences.
    // vizclient returns decoded paths (e.g., raw Kanji), while React Router
    // may provide encoded paths (e.g., %E6%97%A5).
    const normalizedPrevVizPathFromUrl = normalizeVizPathForComparison(prevVizPathFromUrl);
    const normalizedCurrVizPathFromUrl = normalizeVizPathForComparison(currVizPathFromUrl);
    const normalizedVizPathFromAppState = normalizeVizPathForComparison(vizPathFromAppState);

    let currVizParams = this.getVizParamsThatRequireReload(this.props.routeLocationSearch || '');
    let prevVizParams = this.getVizParamsThatRequireReload(prevProps.routeLocationSearch || '');
    const currInstanceId = VizUrl.getInstanceId(this.props.routeLocationSearch);
    const prevInstanceId = VizUrl.getInstanceId(prevProps.routeLocationSearch);

    // there was an instance id but now there isn't, reload the viz which will get an instance id
    const instanceIdWasRemoved = (!currInstanceId && !!prevInstanceId);

    const prevDataAlertId = VizUrl.getVizParams(prevProps.routeLocationSearch)[UrlParamKeys.EditDataAlert];
    const currDataAlertId = VizUrl.getVizParams(this.props.routeLocationSearch)[UrlParamKeys.EditDataAlert];

    if (prevDataAlertId !== currDataAlertId &&
      !_.isUndefined(this.state.viewInfo)  /** need to have a viewinfo to make sure the alert is for the right viz */) {
      if (!_.isUndefined(currDataAlertId)) {
        try {
          const dataAlert: IDataAlertInfo = await DataAlerts.getById(currDataAlertId);
          this.handleGetDataAlertResponse(dataAlert, this.state.viewInfo);
          return;
        } catch (e) {
          this.handleGetDataAlertReturnsError(this.state.viewInfo);
        }
      } else {
        // prevDataAlertId must be removed now so remove the data alert info from the state and continue to
        // rendering and fetching the viz information
        this.updateState({
          ...this.state,
          hasDataAlertError: false,
          dataAlert: undefined
        });
      }
    }

    // if the viz search parameters changed (except for instance id) OR an instance Id was removed,
    // reload the viz. We don't want to reload the viz every time the instance id changes completely because
    // that's what indicates that the viz loaded and this would cause an infinite loop.
    const vizParamsChanged = !_.isEqual(currVizParams, prevVizParams) || instanceIdWasRemoved;

    const dataAlertChanged = !_.isEqual(this.state.dataAlert, prevState.dataAlert);

    // Use normalized paths for comparison to handle URL encoding differences
    if ((normalizedPrevVizPathFromUrl !== normalizedCurrVizPathFromUrl && normalizedCurrVizPathFromUrl !== normalizedVizPathFromAppState) || vizParamsChanged || dataAlertChanged) {

      // Set viz load type to BrowserHistory when the url changes
      if (!VizLoadContextService.has()) {
        VizLoadContextService.set(VizLoadType.BrowserHistory);
      }
      // the url changed (because the app state vizPath is different from the new route props)
      // but the vizPath is not updated yet. So update it and reload the viz.
      // if we are here just because the vizParamsChanged, it doesn't hurt to set the viz path anyway
      this.props.setVizPath(currVizPathFromUrl);


      if (this.vizViewProps) {
        // Use normalized paths for comparison
        if (normalizedCurrVizPathFromUrl !== normalizeVizPathForComparison(this.vizViewProps.viz.path)) {
          this.vizViewProps.onVizPathChange(currVizPathFromUrl);
        }

        this.vizViewProps.viz = {
          path: currVizPathFromUrl,
          vizType: VizType.View,
          params: VizUrl.getVizParams(this.props.routeLocationSearch || ''),
          siteUrlName: this.server.sessionSiteUrlName(),
          instanceId: VizUrl.getInstanceId(this.props.routeLocationSearch),
          isExplicitOriginalViz: VizUrl.getIsExplicitOriginalVizFromUrl(window.location.href),
        };
        this.vizViewProps.displayRoutingError = this.state.hasRoutingError;
        this.vizViewProps.dataAlert = this.state.dataAlert;
        this.vizViewProps.displayDataAlertRoutingError = this.state.hasDataAlertError;
        this.vizViewProps.dalert = this.vizViewProps.dataAlert;

        if (!this.vizViewProps.displayDataAlertRoutingError && !this.vizViewProps.displayRoutingError) {
          this.loadViz();
        }
      } else {
        // since there is no spaceScope, that probably means we were showing the error page before.
        // fetch the view here, the data alert (if needed) and then keep going
        if (_.isUndefined(this.state.viewInfo)) {
          await this.getViewInfoAndUpdateBrowserTitleAndGetDataAlert();
        }
      }

    } else {
      if (
        !this.vizInitialized &&
        this.vizContainer &&
        !this.state.hasDataAlertError &&
        !this.state.hasRoutingError &&
        this.state.viewInfo) {
        // this handles the case that it shows error before and then url is fixed
        // in this case the viz will not be initialized in componentDidMount but here
        this.loadViz();
      }
    }

    if (this.state.initialRender && !_.isUndefined(this.state.viewInfo)) {

      this.setState({ initialRender: false }, () => {
        if (this.props.grantAccessDisplay) {
          RequestAccessAction.handleGrantAccess(this.state.viewInfo, this.props.grantAccessUrl);
          this.resetPreviousUrl();
        }
      });
    }
  }

  public shouldComponentUpdate(nextProps: VizSpaceProviderProps) {
    const nextVizPathFromUrl = nextProps.vizPathFromUrl;
    // Normalize paths for comparison to handle URL encoding differences
    const normalizedNextVizPath = normalizeVizPathForComparison(nextProps.vizPath);
    const normalizedCurrVizPath = normalizeVizPathForComparison(this.props.vizPath);
    const normalizedNextVizPathFromUrl = normalizeVizPathForComparison(nextVizPathFromUrl);

    if (nextProps.vizPath !== VizPathInitialState &&
      normalizedNextVizPath !== normalizedCurrVizPath &&
      normalizedNextVizPath !== normalizedNextVizPathFromUrl) {
      // vizPath changed via user clicking on viz tab (that's why the url viz path does not match the new app state vizPath-- see tbViz),
      // so don't rerender
      return false;
    }
    return true;
  }

  private initVizViewerProps = () => {
    const props = {} as VizViewerProps;
    props.updateVizProviderState = this.updateState;

    props.viz = {
      path: this.props.vizPathFromUrl,
      vizType: VizType.View,
      params: VizUrl.getVizParams(this.props.routeLocationSearch),
      siteUrlName: this.server.sessionSiteUrlName(),
      instanceId: VizUrl.getInstanceId(this.props.routeLocationSearch),
      isExplicitOriginalViz: VizUrl.getIsExplicitOriginalVizFromUrl(window.location.href)
    };

    props.displayRoutingError = this.state.hasRoutingError;
    props.displayDataAlertRoutingError = this.state.hasDataAlertError;
    props.dataAlert = this.state.dataAlert;

    props.vizProps = {};

    function displayInvalidVizError(errorCode: number, vizPath: string) {
      props.displayRoutingError = true;
      props.displayDataAlertRoutingError = false;

      props.updateVizProviderState({
        hasRoutingError: true,
        errorCode: errorCode,
        errorVizPath: vizPath,
        viewInfo: undefined,
        hasDataAlertError: false,
        dataAlertRouterErrorProps: undefined,
      });
    }

    props.onVizPathChange = (newPath: string) => {
      props.viz.path = newPath;

      if (!newPath) {
        displayInvalidVizError(CommonErrorCode.UnexpectedParameterValue, newPath);
        return;
      }
      const searchParams = WindowLocationService.getHashQueryObject();
      Views.getByPath(newPath)
        .then((viewInfo: IViewDetailInfo) => {
          this.handleNewViewInfoReact(viewInfo, props, searchParams);
          // PFT: emit per-sheet Admin Insights 'view' telemetry on sheet switch.
          this.maybeEmitAdminInsightsViewEvent(viewInfo);
        })
        .catch((errorCode: number) => {
          // B153202, TFSID: 463801 - If user does not have permission to view the viz but guest does
          // then we receive a GuestPermittedViewNotFoundError, so we can try to automatically
          // redirect to to the embedded view (if not a site or server admin). But if there's a regular
          // ViewNotFound error, there's no chance for guest to be able to view it, so just show the error here and now.
          this.handleViewGetByPathError(errorCode, displayInvalidVizError, newPath);
        });
    };

    props.onVizInstanceIdChange = (newInstanceId: string) => {

      if (newInstanceId && props.viz.instanceId !== newInstanceId) {
        props.viz.instanceId = newInstanceId;
        VizUrl.updateHashQueryParameter(VizService.InstanceIdKey, newInstanceId);
      }
    };

    props.onVizIsExplicitOriginalVizChange = (isExplicitOriginalViz: boolean) => {
      props.viz.isExplicitOriginalViz = isExplicitOriginalViz;
      isExplicitOriginalViz ? VizUrl.updateHashQueryParameter(VizService.ExplicitOriginalVizParamKey, 'y')
        : VizUrl.deleteHashQueryParameter(VizService.ExplicitOriginalVizParamKey);
    };

    props.clearDataAlertRoute = () => {
      VizUrl.deleteHashQueryParameter(UrlParamKeys.EditDataAlert);
      VizUrl.deleteHashQueryParameter(UrlParamKeys.ResumeDataAlert);
    };

    this.vizViewProps = props;
    return props;
  };

  private handleViewGetByPathError(errorCode: number, displayInvalidVizError: (errorCode: number, vizPath: string) => void, newPath: string) {
    if (errorCode === CommonErrorCode.GuestPermittedViewNotFound && ServerSettingsUnauthenticated.isGuestEnabled() && !this.server.isSessionSiteOrServerAdmin()) {
      this.vizService.redirectToEmbeddedMode(false);
    }
    else {
      displayInvalidVizError(errorCode, newPath);
    }
  }

  /**
   * Fire-and-forget Admin Insights PFT telemetry for a per-sheet (view) open.
   *
   * Emits one `admin_insights_workbook` event with `action: 'view'` carrying the
   * sheet's own name and luid, gated on the parent workbook's project matching the
   * Admin Insights project (the gate lives in emitAdminInsightsContentEvent). A
   * sheet has no project of its own, so we use its parent workbook's project id.
   *
   * Deduped on the view luid: the JS Embedding API drives the path-change callback
   * from TAB_SWITCH *and* the CUSTOM_VIEW_LOAD/SAVE/REMOVE events (which "fire at
   * very strange times" -- see Viz.ts), and the initial load can be followed by an
   * early TAB_SWITCH for the same sheet. Guarding on the last-emitted luid keeps it
   * to one event per distinct sheet landing. Never throws -- telemetry must not
   * disrupt viz navigation.
   */
  private maybeEmitAdminInsightsViewEvent(viewInfo: IViewDetailInfo): void {
    try {
      if (!viewInfo || !viewInfo.luid) {
        return;
      }
      if (viewInfo.luid === this.lastEmittedAdminInsightsViewLuid) {
        return;
      }
      this.lastEmittedAdminInsightsViewLuid = viewInfo.luid;
      const projectId = viewInfo.workbook && viewInfo.workbook.project
        ? viewInfo.workbook.project.id
        : undefined;
      emitAdminInsightsContentEvent(
        'workbook',
        { projectId, luid: viewInfo.luid, name: viewInfo.name },
        'view',
      );
    } catch {
      // Telemetry must never disrupt the primary user flow
    }
  }

  private handleNewViewInfoReact(viewInfo: IViewDetailInfo, vizViewProps: VizViewerProps, searchParams: any) {
    this.loadNewViewInfoReact(viewInfo, vizViewProps);
    if (vizViewProps.displayRoutingError && !vizViewProps.displayDataAlertRoutingError) {
      // We display routing error when we could not fetch the view. Since we could fetch it now,
      // reset the error codes and  update the state since fetching this view was successful.
      // If we displayed a data alert error, that means we fetched the view correctly before.
      vizViewProps.displayRoutingError = false;
      vizViewProps.updateVizProviderState({
        ...initialNoErrorState,
        viewInfo: viewInfo
      });
    }
    // because the viewInfo can return after we have navigated away from this page, we only want to call
    // enterSpace if we are still on the view page. Otherwise, all sorts of errors happen if the SpaceInfo
    // is wrong- TFSID 956506
    if (isCurrentUrlViz()) {
      getAppStore().dispatch(enterSpace({
        item: viewInfo,
        spaceType: 'view',
        subspaceName: undefined
      }));
    }
    const alertId = searchParams[UrlParamKeys.EditDataAlert];
    //if fromEmail param in location.search then log the event
    if (searchParams[UrlParamKeys.FromFailureEmail]) {
      sendVisitAlertFromEmailTelemetry({
        alert_evaluation_state: 'Failure',
        alert_id: alertId,
        view_id: viewInfo.id,
        workbook_id: viewInfo.workbook.id,
        site_role: this.server.sessionSiteRole(),
      });
    }
    if (searchParams[UrlParamKeys.FromSuccessEmail]) {
      sendVisitAlertFromEmailTelemetry({
        alert_evaluation_state: 'Success',
        alert_id: alertId,
        view_id: viewInfo.id,
        workbook_id: viewInfo.workbook.id,
        site_role: this.server.sessionSiteRole(),
      });
    }
    maybeSendTelemetry_favoriteItemOpened(viewInfo);
    UrlTelemetryService.maybeSendDataQualitySubscriptionEvent(viewInfo.id, 'view', searchParams);
  }

  private displayDataAlertError(dataAlertRouterErrorProps: RouterErrorOwnProps, viewInfo: IViewDetailInfo) {

    this.updateState({
      hasRoutingError: false,
      errorCode: undefined,
      errorVizPath: undefined,
      viewInfo: viewInfo,
      hasDataAlertError: true,
      dataAlertRouterErrorProps,
      dataAlert: undefined,
      initialRender: true
    });
  }

  private handleGetDataAlertReturnsError(viewInfo: IViewDetailInfo): void {
    const viewUrl = this.props.urls.viewUrl(this.props.vizPathFromUrl);
    const routerErrorProps: RouterErrorOwnProps = {
      errorType: 'view-data-alert-exists',
      urls: this.props.urls,
      dataAlertErrorData: {
        viewUrl: viewUrl,
        owner: undefined
      }
    };
    this.displayDataAlertError(routerErrorProps, viewInfo);
  }

  private handleGetDataAlertResponse(dataAlert: IDataAlertInfo, viewInfo: IViewDetailInfo): void {
    //Using siteUrls.viewUrl(WindowServiceLocation.getPath) would preserve query string, which won't work
    //So we're explicitly getting the view URL and explicity manually adding the hash
    const viewUrl = this.props.urls.viewUrl(this.props.vizPathFromUrl);
    //we don't want an alert error if there's a view error
    if (!!this.state.hasRoutingError && _.isUndefined(viewInfo)) {
      return;
    }
    if (!dataAlert) {
      const routerErrorProps: RouterErrorOwnProps = {
        errorType: 'view-data-alert-exists',
        urls: this.props.urls,
        dataAlertErrorData: {
          viewUrl: viewUrl,
          owner: undefined
        }
      };
      this.displayDataAlertError(routerErrorProps, viewInfo);
    }
    else if (dataAlert.owner.id !== this.server.sessionUserId() && !this.server.isSessionSiteOrServerAdmin()) {
      const routerErrorProps: RouterErrorOwnProps = {
        errorType: 'view-data-alert-permissions',
        urls: this.props.urls,
        dataAlertErrorData: {
          viewUrl: viewUrl,
          owner: dataAlert.owner
        }
      };
      this.displayDataAlertError(routerErrorProps, viewInfo);
    }
    else if (this.server.isViewerUser()) {
      const routerErrorProps: RouterErrorOwnProps = {
        errorType: 'view-data-alert-viewer',
        urls: this.props.urls,
        dataAlertErrorData: {
          viewUrl: viewUrl,
          owner: undefined
        }
      };
      this.displayDataAlertError(routerErrorProps, viewInfo);
    }
    else {
      // dataAlert successfully fetched and user has access to it-- make sure all errors are reset
      this.updateState({
        ...this.state,
        hasRoutingError: false,
        hasDataAlertError: false,
        dataAlertRouterErrorProps: undefined,
        viewInfo: viewInfo,
        dataAlert: dataAlert
      });
    }
  }

  private async updateBreadcrumbs(viewInfo: IViewDetailInfo) {
    let projectHierarchy: INamedItemInfo[] = [];
    const projectContainer = 'PROJECT';
    if (viewInfo.workbook.project) {
      projectHierarchy = await Projects.getProjectHierarchy(viewInfo.workbook.project.id);
    }

    const projectHierarchyWithType = addContentTypeToProjectHierarchy(projectHierarchy);
    const workbook: IExplorableItemTypeInfo<'workbook'> & IOwnedItemInfo = {
      id: viewInfo.workbook.id,
      name: viewInfo.workbook.name,
      contentType: 'workbook',
      owner: viewInfo.workbook.owner
    };

    const contentPath = (projectHierarchyWithType == null) ? [workbook, viewInfo] : [...projectHierarchyWithType, workbook, viewInfo];
    const requestParam: GetLocationForContentRequestParams = {
      contentType: workbook.contentType,
      contentLuid: viewInfo.workbook.luid
    };
    let contentLocation = undefined;
    try {
      await callViaApiService(requestParam).then(
        async (result: GetLocationForContentResponse) => {
          if (!this.unmounted) {
            contentLocation = await result.contentLocation.type;
            this.populateBreadCrumb(contentPath, contentLocation);
          }
        }
      );
    } catch (e) {
      this.populateBreadCrumb(contentPath, projectContainer);
    }
  }

  private populateBreadCrumb(contentPath: ISpaceTypeInfo[], contentLocation: string) {
    // because the project hierarchy can return after we have navigated away from this page, we only want to call
    // populateContentBreadcrumbs if we are still on the view page. Otherwise, the wrong breadcrumbs show.
    // TFSID 956506
    if (isCurrentUrlViz()) {
      if (contentLocation === 'PERSONAL_SPACE') {
        populateBreadcrumbsForPS(contentPath, this.props.urls);
      } else {
        populateContentBreadcrumbs(contentPath, this.props.urls);
      }
    }
  }

  private async loadNewViewInfoReact(viewInfo: IViewDetailInfo, props: VizViewerProps): Promise<any> {
    BrowserTitleService.setVizTitle(viewInfo.name, viewInfo.workbook.name);
    props.vizProps.view = viewInfo;
    this.updateBreadcrumbs(viewInfo);
    Views.markRecentlyViewed(viewInfo.id);

    return Views.actions(viewInfo.id).then((actions: IViewActions) => {
      props.allowedAction = actions;
    });
  }

  private updateState = (newState: Pick<VizSpaceProviderState, keyof VizSpaceProviderState>): void => {
    if (!this.unmounted) {
      this.setState(newState);
    }
  };

  private resetPreviousUrl = () => {
    const cleanDSUrls = window.location.href;
    this.props.setPreviousUrl(cleanDSUrls);
  };
}

const mapStateToProps: MapStateToProps<VizSpaceProviderStateProps> = (appState) => ({
  vizPath: selectVizPath(appState),
  sessionSiteUrlName: SessionSiteSelectors.siteUrlName(appState),
  layoutType: selectLayoutType(appState),
  isRequestAccessEnabled: SessionSiteSelectors.isRequestAccessEnabled(appState)
});

const mapDispatchToProps: MapDispatchToProps<VizSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  setVizPath,
  setSpaceInfoItem,
  setPreviousUrl,
}, dispatch);

export const ConnectedVizSpaceProvider: React.ComponentClass<VizSpaceProviderOwnProps>
  = connect(mapStateToProps, mapDispatchToProps)(VizSpaceProvider);
